
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { compose } from 'redux';
import injectReducer from '@utils/injectReducer';
import injectSaga from '@utils/injectSaga';
import { withRouter } from 'react-router-dom';
import _ from 'lodash';
import CTAButton from '@components/CTAButton';
import ErrorMessage from '@components/ErrorMessage';
import Text, { TEXT_COLOR, TEXT_SIZE } from '@components/Text';
import TextButton from '@components/TextButton';
import CustomTextInput from '@components/CustomTextInput/index';
import {
  formHasErrors,
  getErrorMessageId,
  fieldTypes,
  getPasswordStrength,
  checkStrengthPassword,
} from '../validate';
import { PasswordHint } from '../PasswordHint';
import {
  ResetPasswordComponentContainer,
  WithHeaderComponentContainer,
  InputContainer,
  ButtonContainer,
  TextContainer,
  ResetPasswordScreenContainer,
  HeaderContainer,
  HintContainer,
  StyledLogo,
  InputContainerAutoHeight,
  LinkContainer,
  StyledLink,
} from './styles';
import LogoImage from '../logoTop.svg';
import { onPasswordReset, clearUiState as clearUiStateAction } from './actions';
import reducer from './reducer';
import saga from './saga';
import { logOut } from '../../../actions';
import { withTranslation } from 'react-i18next';
import { getFromLocalStorage, LOCAL_STORAGE_ITEM } from '@utils/localStorageHelper';
import { isConnectionErrorSentinel, toPlainErrorObject } from '@utils/errorUtils';
import { ROUTES, SUPPORT_EMAIL } from '@shared/constants';
import { cognitoForgotPassword } from '@services/cognitoUserService';
import {
  getCognitoResetPasswordErrorI18nKey,
  getCognitoResetPasswordFieldError,
  normalizeCognitoErrorMessage,
} from '@services/cognitoErrorNormalizer';
import { openPopUp as openPopUpAction } from '../../PopUp/actions';
import POPUP_TYPES from '../../PopUp/types';
import {
  openSupportEmailPopupToSignIn,
} from '../shared/supportPopupHelpers';
import { getCodeSentMessage, normalizeCodeDeliveryDetails } from '../shared/codeDeliveryDetails';
import { getForgotPasswordServerErrorDisposition } from '../ForgotPassword/helpers';

const RESEND_COOLDOWN_MS = 60000;

export const ResetPassword = (props) => {
  const {
    t,
    resetPassword,
    signOut,
    openPopUp,
    redirectToPage,
    serverError,
    firstName,
    lastName,
    history,
    location,
    clearUiState,
  } = props;

  const [authError, setAuthError] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState(-1);
  const [form, setForm] = useState({
    values: {
      verificationCode: '',
      password: '',
      confirmPassword: '',
    },
    errors: {
      verificationCode: null,
      password: null,
      confirmPassword: null,
    },
  });
  const [resendDisabled, setResendDisabled] = useState(false);
  const [resendError, setResendError] = useState(null);
  const [codeWasSent, setCodeWasSent] = useState(Boolean(_.get(location, 'state.codeDeliveryDetails', null)));

  const [codeDeliveryDetails, setCodeDeliveryDetails] = useState(
    normalizeCodeDeliveryDetails(_.get(location, 'state.codeDeliveryDetails', null)),
  );

  const serverErrorObject = useMemo(() => {
    if (!serverError) return null;
    return toPlainErrorObject(serverError);
  }, [serverError]);

  const cognitoChallenge = useMemo(() => {
    const fromState = _.get(location, 'state.cognito', null);
    if (fromState) return fromState;
    return getFromLocalStorage(LOCAL_STORAGE_ITEM.COGNITO_NEW_PASSWORD_CHALLENGE);
  }, [location]);

  const emailFromState = useMemo(() => _.get(location, 'state.email', null), [location]);

  const isCognitoForgotPasswordFlow = useMemo(() => {
    if (cognitoChallenge) return false;
    return Boolean(emailFromState);
  }, [cognitoChallenge, emailFromState]);

  const redirectionReason = useMemo(() => _.get(location, 'state.reason', null), [location]);
  const autoSendCode = useMemo(() => Boolean(_.get(location, 'state.autoSendCode', false)), [location]);
  const didAutoSendRef = useRef(false);
  const didSignOutRef = useRef(false);
  const helpPopupErrorRef = useRef(null);

  const shouldShowHelpPopup = useMemo(() => {
    if (!serverErrorObject) return false;

    const fieldError = getCognitoResetPasswordFieldError(serverErrorObject);
    if (fieldError) return false;

    const i18nKey = getCognitoResetPasswordErrorI18nKey(serverErrorObject);
    if (i18nKey) return false;

    if (isConnectionErrorSentinel(serverErrorObject)) return false;

    return true;
  }, [serverErrorObject]);

  useEffect(() => {
    if (didSignOutRef.current) return;
    if (typeof signOut !== 'function') return;

    didSignOutRef.current = true;

    // This screen is reached from multiple routes/flows. Clearing auth state is desired,
    // but for NEW_PASSWORD_REQUIRED we must not delete the stored challenge payload.
    if (cognitoChallenge) {
      signOut({ preserveCognitoNewPasswordChallenge: true });
      return;
    }

    signOut();
  }, [signOut, cognitoChallenge]);

  useEffect(() => {
    const shouldAutoSend =
      autoSendCode &&
      !didAutoSendRef.current &&
      isCognitoForgotPasswordFlow &&
      Boolean(emailFromState) &&
      redirectionReason === 'passwordExpired';

    if (!shouldAutoSend) return;

    didAutoSendRef.current = true;
    (async () => {
      const result = await cognitoForgotPassword(emailFromState);
      if (result?.success) {
        setCodeWasSent(true);
      }
      const details = normalizeCodeDeliveryDetails(result?.result?.codeDeliveryDetails);
      if (details) setCodeDeliveryDetails(details);
    })();
  }, [autoSendCode, isCognitoForgotPasswordFlow, emailFromState, redirectionReason]);

  useEffect(() => {
    // Successful reset -> navigate to the next screen.
    if (!redirectToPage) return;

    if (history?.push) {
      history.push(redirectToPage);
    }
    clearUiState();
  }, [redirectToPage, history, clearUiState]);

  useEffect(() => {
    if (!serverErrorObject) return;

    const fieldError = getCognitoResetPasswordFieldError(serverErrorObject);
    if (!fieldError?.fieldName || !fieldError?.i18nKey) return;

    setForm((prev) => ({
      ...prev,
      errors: {
        ...prev.errors,
        [fieldError.fieldName]: t(fieldError.i18nKey),
      },
    }));
  }, [serverErrorObject, t]);

  useEffect(() => {
    if (!shouldShowHelpPopup) return;
    if (typeof openPopUp !== 'function') return;

    const signature = `${String(serverErrorObject?.code || '')}|${String(serverErrorObject?.message || '')}`;
    if (helpPopupErrorRef.current === signature) return;
    helpPopupErrorRef.current = signature;

    openSupportEmailPopupToSignIn({
      t,
      titleI18nKey: 'Errors.passwordResetUnavailableTitle',
      bodyI18nKey: 'Errors.passwordResetUnavailableBody',
      email: SUPPORT_EMAIL,
      history,
      openPopUp,
    });
  }, [shouldShowHelpPopup, openPopUp, serverErrorObject, t, history]);

  const onFocus = useCallback((event) => {
    const name = event.target.name;
    setResendError(null);
    setForm((prev) => ({
      ...prev,
      errors: {
        ...prev.errors,
        [name]: null,
      },
    }));
  }, []);

  const onFieldUpdate = useCallback((event) => {
    const { value, name } = event.target;
    setResendError(null);

    if (name === 'password') {
      const strength = value.length
        ? getPasswordStrength(value, _.compact([firstName, lastName]))
        : -1;
      setPasswordStrength(strength);
    }

    setForm((prev) => ({
      ...prev,
      values: {
        ...prev.values,
        [name]: value,
      },
      errors: {
        ...prev.errors,
        [name]: null,
      },
    }));
  }, [firstName, lastName]);

  const getFormErrors = useCallback(() => {
    const { values } = form;
    const newErrors = {};
    const passwordStrengthErrors = checkStrengthPassword(values.password);

    _.forEach(values, (fieldValue, fieldName) => {
      if (fieldName === fieldTypes.verificationCode && !isCognitoForgotPasswordFlow) {
        newErrors[fieldName] = null;
        return;
      }

      const confirmField = fieldName === fieldTypes.confirmPassword ? values.password : null;
      const options = {
        isWeak: fieldName === fieldTypes.password && passwordStrength !== 2,
      };

      const messageId = getErrorMessageId(fieldName, fieldValue, confirmField, options);

      if (
        fieldName === fieldTypes.confirmPassword
        && messageId === 'passwordsNoMatch'
        && passwordStrengthErrors.length
      ) {
        newErrors[fieldName] = null;
        return;
      }

      newErrors[fieldName] = messageId ? t(`Errors.${messageId}`) : null;
    });

    return newErrors;
  }, [form, isCognitoForgotPasswordFlow, passwordStrength, t]);

  const onSubmitButtonClick = useCallback(() => {
    const { values } = form;
    const formErrors = getFormErrors();
    const shouldSubmit = !formHasErrors(formErrors);

    setForm((prev) => ({
      ...prev,
      errors: formErrors,
    }));
    setAuthError(false);

    if (!shouldSubmit) return;

    if (cognitoChallenge) {
      resetPassword(null, values.password, cognitoChallenge);
      return;
    }

    if (isCognitoForgotPasswordFlow) {
      resetPassword(null, values.password, null, { email: emailFromState, code: values.verificationCode });
      return;
    }

    setAuthError(true);
  }, [form, getFormErrors, cognitoChallenge, isCognitoForgotPasswordFlow, resetPassword, emailFromState]);

  const errorMessageNode = useMemo(() => {
    if (authError) {
      return <ErrorMessage type={'CUSTOM'} customText={t('Errors.authError')} />;
    }

    if (resendError) {
      return <ErrorMessage type={'CUSTOM'} customText={resendError} />;
    }

    if (!serverErrorObject) return null;
    if (shouldShowHelpPopup) return null;

    if (isConnectionErrorSentinel(serverErrorObject)) {
      return <ErrorMessage type={'CUSTOM'} customText={t('Errors.connectionErrorCheckInternet')} />;
    }

    // Field-level errors are displayed inline (under the relevant input).
    const fieldError = getCognitoResetPasswordFieldError(serverErrorObject);
    if (fieldError) return null;

    const i18nKey = getCognitoResetPasswordErrorI18nKey(serverErrorObject);
    const msg = i18nKey
      ? t(i18nKey)
      : (normalizeCognitoErrorMessage(serverErrorObject, { defaultMessage: t('Errors.tryAgain') }) || t('Errors.tryAgain'));

    return <ErrorMessage type={'CUSTOM'} customText={msg} />;
  }, [authError, resendError, serverErrorObject, shouldShowHelpPopup, t]);

  const passwordHintNode = useMemo(() => {
    const password = form.values.password;
    const passwordError = form.errors.password;
    const errorsList = checkStrengthPassword(password).map((messageId) => t(`Errors.${messageId}`));
    if (!password.length || passwordError) return null;
    return (
      <HintContainer>
        <PasswordHint strength={passwordStrength} errors={errorsList} />
      </HintContainer>
    );
  }, [form.values.password, form.errors.password, passwordStrength, t]);

  const resendResetCode = useCallback(async () => {
    setResendError(null);

    if (!emailFromState) {
      setResendError(t('Errors.noEmail'));
      return;
    }

    setResendDisabled(true);
    setTimeout(() => setResendDisabled(false), RESEND_COOLDOWN_MS); // Re-enable after 60 seconds

    const result = await cognitoForgotPassword(emailFromState);
    if (!result?.success) {
      const rawErr = result?.error;
      const disposition = getForgotPasswordServerErrorDisposition(rawErr);

      if (disposition.kind === 'HELP_POPUP') {
        openSupportEmailPopupToSignIn({
          t,
          titleI18nKey: 'Errors.passwordResetUnavailableTitle',
          bodyI18nKey: 'Errors.passwordResetUnavailableBody',
          email: SUPPORT_EMAIL,
          history,
          openPopUp,
        });
        return;
      }

      const i18nKey = getCognitoResetPasswordErrorI18nKey(rawErr);
      const msg = isConnectionErrorSentinel(rawErr)
        ? t('Errors.connectionErrorCheckInternet')
        : (i18nKey ? t(i18nKey) : (normalizeCognitoErrorMessage(rawErr, { defaultMessage: t('Errors.tryAgain') }) || t('Errors.tryAgain')));

      setResendError(msg);
      return;
    }

    setCodeWasSent(true);
    setResendError(null);

    const details = normalizeCodeDeliveryDetails(result?.result?.codeDeliveryDetails);
    if (details) setCodeDeliveryDetails(details);
  }, [emailFromState, history, openPopUp, t]);

  const header = t('Authentication.Password.reset');

  const codeSentMessage = useMemo(() => {
    if (!isCognitoForgotPasswordFlow) return null;
    if (!codeWasSent) return null;
    return getCodeSentMessage({ t, codeDeliveryDetails });
  }, [isCognitoForgotPasswordFlow, codeWasSent, t, codeDeliveryDetails]);

  const commonProps = {
    type: 'password',
    onChange: onFieldUpdate,
    onFocus,
    onEnter: onSubmitButtonClick,
  };

  return (
    <ResetPasswordScreenContainer>
      <WithHeaderComponentContainer>
        <HeaderContainer>
          <StyledLogo src={LogoImage} alt='logo' />
          <Text textColor={TEXT_COLOR.DARK_BLUE} size={TEXT_SIZE.H1}>
            {header}
          </Text>
        </HeaderContainer>
        <ResetPasswordComponentContainer>
          {errorMessageNode}

          <InputContainerAutoHeight>
            {isCognitoForgotPasswordFlow && (
              <CustomTextInput
                id='verification_code'
                label={t('Authentication.Verification.enterCode')}
                name='verificationCode'
                error={form.errors.verificationCode}
                value={form.values.verificationCode}
                onChange={onFieldUpdate}
                onFocus={onFocus}
                onEnter={onSubmitButtonClick}
                autofocus
              />
            )}

            {isCognitoForgotPasswordFlow && codeSentMessage && (
              <TextContainer>
                <Text textColor={TEXT_COLOR.DISABLED} size={TEXT_SIZE.H4}>
                  {codeSentMessage}
                </Text>
              </TextContainer>
            )}
            <CustomTextInput
              type="password"
              {...commonProps}
              id='password'
              label={t('Authentication.Password.newPassword')}
              name='password'
              error={form.errors.password}
              value={form.values.password}
              autofocus={!isCognitoForgotPasswordFlow}
              showPasswordToggle
            />
            {passwordHintNode}
          </InputContainerAutoHeight>

          <InputContainer>
            <CustomTextInput
              type="password"
              {...commonProps}
              id='confirm-password'
              label={t('Authentication.Password.confirm')}
              name='confirmPassword'
              error={form.errors.confirmPassword}
              value={form.values.confirmPassword}
            />
          </InputContainer>

          <ButtonContainer>
            <CTAButton
              id='submit_button'
              expanded
              onClick={onSubmitButtonClick}
            >
              {t('Actions.Instance.submit')}
            </CTAButton>
          </ButtonContainer>
        </ResetPasswordComponentContainer>

        <LinkContainer>
          <StyledLink id='sign_in_link' to={ROUTES.SIGN_IN}>
            <TextButton>
              <Text size={TEXT_SIZE.H4}>
                {t('Authentication.Distribution.backToSignIn')}
              </Text>
            </TextButton>
          </StyledLink>

          {isCognitoForgotPasswordFlow && (
            <TextButton
              id='resend_code_link'
              disabled={resendDisabled}
              onClick={resendResetCode}
              style={{ padding: '0 5px', opacity: resendDisabled ? 0.5 : 1 }}
            >
              <Text size={TEXT_SIZE.H4}>{t('Authentication.Email.resend')}</Text>
            </TextButton>
          )}
        </LinkContainer>
      </WithHeaderComponentContainer>
    </ResetPasswordScreenContainer>
  );
};

ResetPassword.defaultProps = {
  resetPassword: () => {},
  signOut: () => {},
  openPopUp: null,
  serverError: null,
  redirectToPage: null,
  firstName: '',
  lastName: '',
  history: null,
  location: null,
  t: (key) => key,
};

ResetPassword.propTypes = {
  resetPassword: PropTypes.func.isRequired,
  // from react-router
  match: PropTypes.object,
  history: PropTypes.object,
  location: PropTypes.object,
  // from redux store
  serverError: PropTypes.any,
  firstName: PropTypes.string,
  lastName: PropTypes.string,
  signOut: PropTypes.func,
  openPopUp: PropTypes.func,
};

export const mapStateToProps = (state) => {
  const serverError = state.getIn(['resetPassword', 'errors', 'serverError']);
  const redirectToPage = state.getIn(['resetPassword', 'redirectToPage']);
  const firstName = state.getIn(['app', 'authentication', 'user', 'firstName'], '');
  const lastName = state.getIn(['app', 'authentication', 'user', 'lastName'], '');
  return { serverError, redirectToPage, firstName, lastName };
};

export const mapDispatchToProps = (dispatch) => ({
  resetPassword: (
    recoveryToken,
    password,
    cognitoChallenge,
    cognitoForgotPassword,
  ) => {
    dispatch(
      onPasswordReset(
        recoveryToken,
        password,
        cognitoChallenge,
        cognitoForgotPassword,
      ),
    );
  },
  signOut: (data) => {
    dispatch(logOut(data));
  },
  openPopUp: (props) => dispatch(openPopUpAction(POPUP_TYPES.ALERT, props)),
  clearUiState: () => dispatch(clearUiStateAction()),
});

const withConnect = connect(mapStateToProps, mapDispatchToProps);
const withSaga = injectSaga({ key: 'resetPassword', saga });
const withReducer = injectReducer({ key: 'resetPassword', reducer });

export default compose(
  withConnect,
  withRouter,
  withReducer,
  withSaga,
  withTranslation(),
)(ResetPassword);
